/**
 * UNIFIED TRIP DETAILS TAB - COMPLETE REARCHITECTURE
 * 
 * System Rearchitecture - ONE unified section containing:
 * 1. Traveler Profiles (CREATE + LINK to master Client Database) with BOOKING-READY status
 * 2. Trip Identity (auto-generated name, stable ID)
 * 3. International Flag (controls passport validation)
 * 4. Budget Tracker (structured, categories, line items, alerts, auto-recalculating)
 * 
 * REMOVED from this tab (relocated):
 * - Agreement Summary → Agreements tab
 * - Reveal Mode → Itinerary tab toggle
 * - Weather → Logistics tab
 * - AI Tools → AI Assistant tab
 * - Itinerary Summary → Itinerary tab
 * - Flights → Logistics tab
 * - Lodging → Logistics tab
 * - Prices Summary → Trip Estimate tab
 * - Client Communications → Client Lounge tab
 * - Client Landing Page → Itinerary tab
 * - Planning Fee → Trip Estimate tab
 * - Consult Notes Synopsis → Expandable reference
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, X, Check, ChevronDown, ChevronUp, Search, Link2,
  Plane, Shield, AlertCircle, Calendar, DollarSign, FileText,
  User, Mail, Phone, Loader2, Edit2, Trash2, Save, ExternalLink,
  MapPin, Globe, Sparkles, Home, AlertTriangle, CheckCircle2,
  CreditCard, BadgeCheck, Clock, MoreHorizontal, Image
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { DestinationSegmentsForm } from './DestinationSegmentsForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Travel style options
const TRAVEL_STYLE_OPTIONS = [
  { value: 'romantic', label: 'Romantic' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'relaxation', label: 'Relaxation' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'family', label: 'Family-Friendly' },
  { value: 'foodie', label: 'Foodie/Culinary' },
  { value: 'nature', label: 'Nature' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'historical', label: 'Historical' }
];

// Trip type options
const TRIP_TYPE_OPTIONS = [
  { value: 'solo', label: 'Solo' },
  { value: 'couple', label: 'Couple' },
  { value: 'family', label: 'Family' },
  { value: 'group', label: 'Group' },
  { value: 'business', label: 'Business' }
];

// Budget categories with labels and icons
const BUDGET_CATEGORIES = [
  { key: 'flights', label: 'Flights', icon: Plane },
  { key: 'lodging', label: 'Lodging', icon: Home },
  { key: 'transfers_transport', label: 'Transfers/Transport', icon: MapPin },
  { key: 'activities_experiences', label: 'Activities/Experiences', icon: Sparkles },
  { key: 'dining', label: 'Dining', icon: CreditCard },
  { key: 'misc_fees', label: 'Misc/Fees', icon: FileText }
];

// ============================================================
// BOOKING-READY INDICATOR COMPONENT
// ============================================================
function BookingReadyIndicator({ status, compact = false }) {
  if (!status) return null;
  
  const { is_booking_ready, completeness_percentage, missing_required_fields } = status;
  
  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        is_booking_ready 
          ? 'bg-[#8DA399]/10 text-[#8DA399]' 
          : 'bg-[#B37E6B]/10 text-[#B37E6B]'
      }`}>
        {is_booking_ready ? (
          <>
            <CheckCircle2 className="w-3 h-3" />
            Booking Ready
          </>
        ) : (
          <>
            <AlertTriangle className="w-3 h-3" />
            {missing_required_fields?.length || 0} Missing
          </>
        )}
      </span>
    );
  }
  
  return (
    <div className={`p-3 rounded-lg border ${
      is_booking_ready 
        ? 'bg-[#8DA399]/5 border-[#8DA399]/20' 
        : 'bg-[#B37E6B]/5 border-[#B37E6B]/20'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-[#8C8C8C]">
          Booking Ready Status
        </span>
        <span className={`text-sm font-medium ${
          is_booking_ready ? 'text-[#8DA399]' : 'text-[#B37E6B]'
        }`}>
          {completeness_percentage}%
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="h-1.5 bg-[#E6E4DE] rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full transition-all ${is_booking_ready ? 'bg-[#8DA399]' : 'bg-[#B37E6B]'}`}
          style={{ width: `${completeness_percentage}%` }}
        />
      </div>
      
      {!is_booking_ready && missing_required_fields?.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-[#B37E6B] mb-1">Missing fields:</p>
          <div className="flex flex-wrap gap-1">
            {missing_required_fields.map(field => (
              <span key={field} className="text-xs px-1.5 py-0.5 bg-[#B37E6B]/10 text-[#B37E6B] rounded">
                {field.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TRAVELER PROFILE MANAGER (COMPLETE REWRITE)
// ============================================================
function TravelerProfileManager({ tripId, travelers, onTravelersChange, isInternational, travelPartyId }) {
  const navigate = useNavigate();
  const [showAddTraveler, setShowAddTraveler] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [expandedTraveler, setExpandedTraveler] = useState(null);
  const [editingTraveler, setEditingTraveler] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [createMode, setCreateMode] = useState('search'); // 'search' or 'create'
  const [newTraveler, setNewTraveler] = useState({
    legal_full_name: '',
    preferred_name: '',
    date_of_birth: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    passport_number: '',
    passport_issuing_country: '',
    passport_expiration_date: '',
    known_traveler_number: '',
    redress_number: '',
    airline_preferences: '',
    seat_preference: '',
    airport_preferences: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    dietary_needs: '',
    accessibility_notes: '',
    notes: ''
  });
  
  // Search for existing travelers/clients in database
  const searchTravelers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/travels/search?q=${encodeURIComponent(query)}`);
      setSearchResults([
        ...(res.data.clients || []).map(c => ({ ...c, type: 'client' })),
        ...(res.data.travelers || []).map(t => ({ ...t, type: 'traveler' }))
      ]);
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setSearching(false);
    }
  };
  
  // Link existing traveler to trip
  const linkTraveler = async (person) => {
    try {
      // If it's a client without a traveler profile, create one first
      let travelerId = person.id;
      
      if (person.type === 'client') {
        // Create traveler profile from client
        const travelerData = {
          legal_full_name: person.full_name,
          email: person.email,
          phone: person.phone,
          household_client_id: person.id
        };
        const createRes = await axios.post(`${API}/travelers`, travelerData);
        travelerId = createRes.data.id;
      }
      
      // Link to trip
      await axios.post(`${API}/travels/trips/${tripId}/link-traveler?traveler_id=${travelerId}`);
      toast.success(`Linked ${person.full_name || person.legal_full_name} to trip`);
      onTravelersChange?.();
      setShowAddTraveler(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link traveler');
    }
  };
  
  // Create new traveler and link to trip
  const createTraveler = async () => {
    if (!newTraveler.legal_full_name) {
      toast.error('Legal full name is required');
      return;
    }
    
    try {
      // Create traveler profile in master database
      const createRes = await axios.post(`${API}/travelers`, newTraveler);
      const travelerId = createRes.data.id;
      
      // Link to trip
      await axios.post(`${API}/travels/trips/${tripId}/link-traveler?traveler_id=${travelerId}`);
      toast.success(`Created and linked ${newTraveler.legal_full_name}`);
      onTravelersChange?.();
      setShowAddTraveler(false);
      resetNewTravelerForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create traveler');
    }
  };
  
  const resetNewTravelerForm = () => {
    setNewTraveler({
      legal_full_name: '',
      preferred_name: '',
      date_of_birth: '',
      email: '',
      phone: '',
      street_address: '',
      city: '',
      state_province: '',
      postal_code: '',
      country: '',
      passport_number: '',
      passport_issuing_country: '',
      passport_expiration_date: '',
      known_traveler_number: '',
      redress_number: '',
      airline_preferences: '',
      seat_preference: '',
      airport_preferences: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      dietary_needs: '',
      accessibility_notes: '',
      notes: ''
    });
    setCreateMode('search');
  };
  
  // Update traveler in master database
  const updateTraveler = async (travelerId) => {
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/travelers/${travelerId}`, editForm);
      toast.success('Traveler updated');
      onTravelersChange?.();
      setEditingTraveler(null);
      setEditForm({});
    } catch (e) {
      toast.error('Failed to update traveler');
    } finally {
      setSavingEdit(false);
    }
  };
  
  // Unlink traveler from trip
  const unlinkTraveler = async (travelerId, travelerName) => {
    if (!confirm(`Remove ${travelerName} from this trip?`)) return;
    try {
      await axios.post(`${API}/travels/trips/${tripId}/unlink-traveler?traveler_id=${travelerId}`);
      toast.success('Traveler removed from trip');
      onTravelersChange?.();
    } catch (e) {
      toast.error('Failed to remove traveler');
    }
  };
  
  // Start editing a traveler
  const startEditing = (traveler) => {
    setEditingTraveler(traveler.id);
    setEditForm({ ...traveler });
    setExpandedTraveler(traveler.id);
  };
  
  // Check passport validity
  const getPassportStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'missing', message: 'No passport info', color: 'text-[#B37E6B] bg-[#B37E6B]/10' };
    const expiry = new Date(expiryDate);
    const today = new Date();
    const monthsUntil = (expiry - today) / (1000 * 60 * 60 * 24 * 30);
    
    if (monthsUntil < 0) return { status: 'expired', message: 'Expired', color: 'text-red-600 bg-red-50' };
    if (monthsUntil < 6) return { status: 'critical', message: '<6 months', color: 'text-red-600 bg-red-50' };
    if (monthsUntil < 9) return { status: 'warning', message: '<9 months', color: 'text-amber-600 bg-amber-50' };
    return { status: 'valid', message: 'Valid', color: 'text-[#8DA399] bg-[#8DA399]/10' };
  };
  
  // Navigate to master client profile for this traveler's household
  const openTravelerProfile = (travelerId) => {
    const traveler = travelers?.find(t => t.id === travelerId);
    const clientId = traveler?.household_client_id;
    if (clientId) {
      navigate(`/advisor/manage/clients/${clientId}`);
    } else {
      navigate('/advisor/manage/clients');
    }
  };
  
  return (
    <div className="space-y-4" data-testid="traveler-profile-manager">
      {/* Travel Party Badge */}
      {travelPartyId && (
        <div className="flex items-center gap-2 text-xs text-[#8C8C8C]">
          <Users className="w-3 h-3" />
          <span>Travel Party: {travelPartyId.slice(0, 8)}...</span>
        </div>
      )}
      
      {/* Travelers List */}
      <div className="space-y-3">
        {travelers?.length === 0 ? (
          <div className="text-center py-8 bg-[#F9F8F4] rounded-lg">
            <Users className="w-8 h-8 text-[#8C8C8C] mx-auto mb-2" />
            <p className="text-sm text-[#8C8C8C]">No travelers linked yet</p>
            <Button 
              size="sm" 
              onClick={() => setShowAddTraveler(true)}
              className="mt-3 bg-[#C8A85A] hover:bg-[#B39049]"
              data-testid="add-first-traveler-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Traveler
            </Button>
          </div>
        ) : (
          travelers?.map(traveler => {
            const passportStatus = isInternational ? getPassportStatus(traveler.passport_expiration_date) : null;
            const isExpanded = expandedTraveler === traveler.id;
            const isEditing = editingTraveler === traveler.id;
            const bookingReady = traveler.booking_ready_status;
            
            return (
              <div 
                key={traveler.id}
                className={`bg-white rounded-xl border ${
                  bookingReady && !bookingReady.is_booking_ready
                    ? 'border-[#B37E6B]/30' 
                    : 'border-[#E6E4DE]'
                } overflow-hidden`}
                data-testid={`traveler-card-${traveler.id}`}
              >
                {/* Collapsed View */}
                <button
                  onClick={() => setExpandedTraveler(isExpanded ? null : traveler.id)}
                  className="w-full p-4 text-left flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#F5F1EB] flex items-center justify-center">
                      <User className="w-5 h-5 text-[#8C8C8C]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A]">
                        {traveler.legal_full_name || traveler.full_name}
                        {traveler.preferred_name && traveler.preferred_name !== traveler.legal_full_name && (
                          <span className="text-[#8C8C8C] font-normal"> ({traveler.preferred_name})</span>
                        )}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-[#8C8C8C]">
                        {traveler.email && <span>{traveler.email}</span>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Booking Ready Indicator */}
                    <BookingReadyIndicator status={bookingReady} compact />
                    
                    {/* Passport Status for International */}
                    {isInternational && passportStatus && (
                      <span className={`px-2 py-1 text-xs rounded-full ${passportStatus.color}`}>
                        <Shield className="w-3 h-3 inline mr-1" />
                        {passportStatus.message}
                      </span>
                    )}
                    
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>
                
                {/* Expanded View */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-[#E6E4DE]"
                    >
                      <div className="p-4 space-y-4 bg-[#F9F8F4]">
                        {/* Booking Ready Status */}
                        {bookingReady && (
                          <BookingReadyIndicator status={bookingReady} />
                        )}
                        
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-[#8C8C8C]">Legal Full Name</Label>
                            {isEditing ? (
                              <Input 
                                value={editForm.legal_full_name || ''} 
                                onChange={(e) => setEditForm({...editForm, legal_full_name: e.target.value})}
                                className="mt-1 bg-white"
                              />
                            ) : (
                              <p className="text-sm">{traveler.legal_full_name || '-'}</p>
                            )}
                          </div>
                          <div>
                            <Label className="text-xs text-[#8C8C8C]">Date of Birth</Label>
                            {isEditing ? (
                              <Input 
                                type="date"
                                value={editForm.date_of_birth || ''} 
                                onChange={(e) => setEditForm({...editForm, date_of_birth: e.target.value})}
                                className="mt-1 bg-white"
                              />
                            ) : (
                              <p className="text-sm">{traveler.date_of_birth || '-'}</p>
                            )}
                          </div>
                          <div>
                            <Label className="text-xs text-[#8C8C8C]">Email</Label>
                            {isEditing ? (
                              <Input 
                                type="email"
                                value={editForm.email || ''} 
                                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                className="mt-1 bg-white"
                              />
                            ) : (
                              <p className="text-sm">{traveler.email || '-'}</p>
                            )}
                          </div>
                          <div>
                            <Label className="text-xs text-[#8C8C8C]">Phone</Label>
                            {isEditing ? (
                              <Input 
                                value={editForm.phone || ''} 
                                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                                className="mt-1 bg-white"
                              />
                            ) : (
                              <p className="text-sm">{traveler.phone || '-'}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Address Section */}
                        <div className="pt-3 border-t border-[#E6E4DE]">
                          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Home className="w-3 h-3" /> Residential Address
                          </p>
                          {isEditing ? (
                            <div className="grid grid-cols-2 gap-2">
                              <div className="col-span-2">
                                <Input 
                                  placeholder="Street Address"
                                  value={editForm.street_address || ''} 
                                  onChange={(e) => setEditForm({...editForm, street_address: e.target.value})}
                                  className="bg-white"
                                />
                              </div>
                              <Input 
                                placeholder="City"
                                value={editForm.city || ''} 
                                onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                                className="bg-white"
                              />
                              <Input 
                                placeholder="State/Province"
                                value={editForm.state_province || ''} 
                                onChange={(e) => setEditForm({...editForm, state_province: e.target.value})}
                                className="bg-white"
                              />
                              <Input 
                                placeholder="Postal Code"
                                value={editForm.postal_code || ''} 
                                onChange={(e) => setEditForm({...editForm, postal_code: e.target.value})}
                                className="bg-white"
                              />
                              <Input 
                                placeholder="Country"
                                value={editForm.country || ''} 
                                onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                                className="bg-white"
                              />
                            </div>
                          ) : (
                            <p className="text-sm">
                              {[
                                traveler.street_address,
                                traveler.city,
                                traveler.state_province,
                                traveler.postal_code,
                                traveler.country
                              ].filter(Boolean).join(', ') || '-'}
                            </p>
                          )}
                        </div>
                        
                        {/* Passport Info (if international) */}
                        {isInternational && (
                          <div className="pt-3 border-t border-[#E6E4DE]">
                            <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2 flex items-center gap-1">
                              <Shield className="w-3 h-3" /> Passport Information
                            </p>
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <Label className="text-xs text-[#8C8C8C]">Passport #</Label>
                                {isEditing ? (
                                  <Input 
                                    value={editForm.passport_number || ''} 
                                    onChange={(e) => setEditForm({...editForm, passport_number: e.target.value})}
                                    className="mt-1 bg-white"
                                  />
                                ) : (
                                  <p className="text-sm font-mono">{traveler.passport_number || '-'}</p>
                                )}
                              </div>
                              <div>
                                <Label className="text-xs text-[#8C8C8C]">Expiration</Label>
                                {isEditing ? (
                                  <Input 
                                    type="date"
                                    value={editForm.passport_expiration_date || ''} 
                                    onChange={(e) => setEditForm({...editForm, passport_expiration_date: e.target.value})}
                                    className="mt-1 bg-white"
                                  />
                                ) : (
                                  <p className={`text-sm ${passportStatus?.color?.split(' ')[0]}`}>
                                    {traveler.passport_expiration_date || '-'}
                                  </p>
                                )}
                              </div>
                              <div>
                                <Label className="text-xs text-[#8C8C8C]">Issuing Country</Label>
                                {isEditing ? (
                                  <Input 
                                    value={editForm.passport_issuing_country || ''} 
                                    onChange={(e) => setEditForm({...editForm, passport_issuing_country: e.target.value})}
                                    className="mt-1 bg-white"
                                  />
                                ) : (
                                  <p className="text-sm">{traveler.passport_issuing_country || '-'}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* TSA / Known Traveler */}
                        <div className="pt-3 border-t border-[#E6E4DE]">
                          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2 flex items-center gap-1">
                            <BadgeCheck className="w-3 h-3" /> TSA / Secure Flight
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-[#8C8C8C]">Known Traveler #</Label>
                              {isEditing ? (
                                <Input 
                                  value={editForm.known_traveler_number || ''} 
                                  onChange={(e) => setEditForm({...editForm, known_traveler_number: e.target.value})}
                                  className="mt-1 bg-white"
                                  placeholder="TSA PreCheck/Global Entry"
                                />
                              ) : (
                                <p className="text-sm font-mono">{traveler.known_traveler_number || '-'}</p>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs text-[#8C8C8C]">Redress #</Label>
                              {isEditing ? (
                                <Input 
                                  value={editForm.redress_number || ''} 
                                  onChange={(e) => setEditForm({...editForm, redress_number: e.target.value})}
                                  className="mt-1 bg-white"
                                />
                              ) : (
                                <p className="text-sm font-mono">{traveler.redress_number || '-'}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Preferences */}
                        <div className="pt-3 border-t border-[#E6E4DE]">
                          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2">Travel Preferences</p>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label className="text-xs text-[#8C8C8C]">Airline</Label>
                              {isEditing ? (
                                <Input 
                                  value={editForm.airline_preferences || ''} 
                                  onChange={(e) => setEditForm({...editForm, airline_preferences: e.target.value})}
                                  className="mt-1 bg-white"
                                />
                              ) : (
                                <p className="text-sm">{traveler.airline_preferences || '-'}</p>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs text-[#8C8C8C]">Seat</Label>
                              {isEditing ? (
                                <Select value={editForm.seat_preference || ''} onValueChange={(v) => setEditForm({...editForm, seat_preference: v})}>
                                  <SelectTrigger className="mt-1 bg-white">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="window">Window</SelectItem>
                                    <SelectItem value="aisle">Aisle</SelectItem>
                                    <SelectItem value="middle">Middle</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <p className="text-sm capitalize">{traveler.seat_preference || '-'}</p>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs text-[#8C8C8C]">Home Airport</Label>
                              {isEditing ? (
                                <Input 
                                  value={editForm.airport_preferences || ''} 
                                  onChange={(e) => setEditForm({...editForm, airport_preferences: e.target.value})}
                                  className="mt-1 bg-white"
                                  placeholder="e.g., LAX"
                                />
                              ) : (
                                <p className="text-sm">{traveler.airport_preferences || '-'}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Emergency Contact */}
                        <div className="pt-3 border-t border-[#E6E4DE]">
                          <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> Emergency Contact
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-[#8C8C8C]">Name</Label>
                              {isEditing ? (
                                <Input 
                                  value={editForm.emergency_contact_name || ''} 
                                  onChange={(e) => setEditForm({...editForm, emergency_contact_name: e.target.value})}
                                  className="mt-1 bg-white"
                                />
                              ) : (
                                <p className="text-sm">{traveler.emergency_contact_name || '-'}</p>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs text-[#8C8C8C]">Phone</Label>
                              {isEditing ? (
                                <Input 
                                  value={editForm.emergency_contact_phone || ''} 
                                  onChange={(e) => setEditForm({...editForm, emergency_contact_phone: e.target.value})}
                                  className="mt-1 bg-white"
                                />
                              ) : (
                                <p className="text-sm">{traveler.emergency_contact_phone || '-'}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex justify-between items-center pt-3 border-t border-[#E6E4DE]">
                          <button
                            onClick={() => openTravelerProfile(traveler.id)}
                            className="text-xs text-[#C8A85A] hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Master Profile
                          </button>
                          
                          <div className="flex gap-2">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingTraveler(null);
                                    setEditForm({});
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => updateTraveler(traveler.id)}
                                  disabled={savingEdit}
                                  className="bg-[#8DA399] hover:bg-[#7A9389]"
                                >
                                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                                  Save
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEditing(traveler)}
                                >
                                  <Edit2 className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => unlinkTraveler(traveler.id, traveler.legal_full_name || traveler.full_name)}
                                  className="text-[#B37E6B] hover:bg-[#B37E6B]/10"
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Remove
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>
      
      {/* Add Traveler Button */}
      {travelers?.length > 0 && !showAddTraveler && (
        <Button 
          variant="outline" 
          onClick={() => setShowAddTraveler(true)}
          className="w-full border-dashed"
          data-testid="add-another-traveler-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Traveler
        </Button>
      )}
      
      {/* Add Traveler Panel */}
      <AnimatePresence>
        {showAddTraveler && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border border-[#C8A85A] rounded-xl overflow-hidden bg-white"
          >
            <div className="p-4 bg-[#C8A85A]/5 border-b border-[#C8A85A]/20">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#C8A85A]" />
                  Add Traveler
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCreateMode('search')}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      createMode === 'search' 
                        ? 'bg-[#C8A85A] text-white' 
                        : 'bg-[#E6E4DE] text-[#8C8C8C]'
                    }`}
                  >
                    Search Existing
                  </button>
                  <button
                    onClick={() => setCreateMode('create')}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      createMode === 'create' 
                        ? 'bg-[#C8A85A] text-white' 
                        : 'bg-[#E6E4DE] text-[#8C8C8C]'
                    }`}
                  >
                    Create New
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              {createMode === 'search' ? (
                <>
                  {/* Search Existing */}
                  <div>
                    <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">
                      Search Existing Clients/Travelers
                    </Label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          searchTravelers(e.target.value);
                        }}
                        placeholder="Search by name or email..."
                        className="pl-9"
                        data-testid="search-traveler-input"
                      />
                      {searching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                      )}
                    </div>
                    
                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg">
                        {searchResults.map((result, idx) => (
                          <button
                            key={result.id || idx}
                            onClick={() => linkTraveler(result)}
                            className="w-full p-3 text-left hover:bg-[#F5F1EB] border-b last:border-b-0 flex items-center justify-between"
                            data-testid={`search-result-${result.id}`}
                          >
                            <div>
                              <p className="text-sm font-medium">
                                {result.full_name || result.legal_full_name}
                              </p>
                              <p className="text-xs text-[#8C8C8C]">{result.email}</p>
                            </div>
                            <span className="text-xs px-2 py-0.5 bg-[#C8A85A]/10 text-[#C8A85A] rounded">
                              {result.type === 'client' ? 'Client' : 'Traveler'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                      <p className="text-sm text-[#8C8C8C] mt-2">No results found. Try creating a new traveler.</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Create New Traveler Form - Booking Ready Fields */}
                  <div className="space-y-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label className="text-xs text-[#8C8C8C]">Legal Full Name *</Label>
                        <Input
                          value={newTraveler.legal_full_name}
                          onChange={(e) => setNewTraveler({ ...newTraveler, legal_full_name: e.target.value })}
                          placeholder="As on passport"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#8C8C8C]">Email *</Label>
                        <Input
                          type="email"
                          value={newTraveler.email}
                          onChange={(e) => setNewTraveler({ ...newTraveler, email: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#8C8C8C]">Phone *</Label>
                        <Input
                          value={newTraveler.phone}
                          onChange={(e) => setNewTraveler({ ...newTraveler, phone: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#8C8C8C]">Date of Birth *</Label>
                        <Input
                          type="date"
                          value={newTraveler.date_of_birth}
                          onChange={(e) => setNewTraveler({ ...newTraveler, date_of_birth: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#8C8C8C]">Preferred Name</Label>
                        <Input
                          value={newTraveler.preferred_name}
                          onChange={(e) => setNewTraveler({ ...newTraveler, preferred_name: e.target.value })}
                          placeholder="Nickname"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    {/* Address */}
                    <div className="border-t border-[#E6E4DE] pt-3">
                      <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2 block">
                        Residential Address *
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-2">
                          <Input
                            placeholder="Street Address"
                            value={newTraveler.street_address}
                            onChange={(e) => setNewTraveler({ ...newTraveler, street_address: e.target.value })}
                          />
                        </div>
                        <Input
                          placeholder="City"
                          value={newTraveler.city}
                          onChange={(e) => setNewTraveler({ ...newTraveler, city: e.target.value })}
                        />
                        <Input
                          placeholder="State/Province"
                          value={newTraveler.state_province}
                          onChange={(e) => setNewTraveler({ ...newTraveler, state_province: e.target.value })}
                        />
                        <Input
                          placeholder="Postal Code"
                          value={newTraveler.postal_code}
                          onChange={(e) => setNewTraveler({ ...newTraveler, postal_code: e.target.value })}
                        />
                        <Input
                          placeholder="Country"
                          value={newTraveler.country}
                          onChange={(e) => setNewTraveler({ ...newTraveler, country: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    {/* Passport (if international) */}
                    {isInternational && (
                      <div className="border-t border-[#E6E4DE] pt-3">
                        <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2 block">
                          Passport Information *
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            placeholder="Passport Number"
                            value={newTraveler.passport_number}
                            onChange={(e) => setNewTraveler({ ...newTraveler, passport_number: e.target.value })}
                          />
                          <Input
                            type="date"
                            placeholder="Expiration"
                            value={newTraveler.passport_expiration_date}
                            onChange={(e) => setNewTraveler({ ...newTraveler, passport_expiration_date: e.target.value })}
                          />
                          <Input
                            placeholder="Issuing Country"
                            value={newTraveler.passport_issuing_country}
                            onChange={(e) => setNewTraveler({ ...newTraveler, passport_issuing_country: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Emergency Contact */}
                    <div className="border-t border-[#E6E4DE] pt-3">
                      <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2 block">
                        Emergency Contact (Recommended)
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Contact Name"
                          value={newTraveler.emergency_contact_name}
                          onChange={(e) => setNewTraveler({ ...newTraveler, emergency_contact_name: e.target.value })}
                        />
                        <Input
                          placeholder="Contact Phone"
                          value={newTraveler.emergency_contact_phone}
                          onChange={(e) => setNewTraveler({ ...newTraveler, emergency_contact_phone: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E6E4DE]">
                <Button variant="outline" onClick={() => { setShowAddTraveler(false); resetNewTravelerForm(); }}>
                  Cancel
                </Button>
                {createMode === 'create' && (
                  <Button onClick={createTraveler} className="bg-[#C8A85A] hover:bg-[#B39049]" data-testid="create-traveler-btn">
                    <Check className="w-4 h-4 mr-1" />
                    Create & Link
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================
// STRUCTURED BUDGET TRACKER (COMPLETE REWRITE)
// ============================================================
function StructuredBudgetTracker({ tripId, onNavigate }) {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showLineItems, setShowLineItems] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    category: 'misc_fees',
    estimated_cost: 0,
    quantity: 1,
    notes: ''
  });
  
  useEffect(() => {
    if (tripId) fetchBudget();
  }, [tripId]);
  
  const fetchBudget = async () => {
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/budget`);
      setBudget(res.data);
    } catch (e) {
      console.error('Failed to fetch budget:', e);
    } finally {
      setLoading(false);
    }
  };
  
  const updateBudget = async (updates) => {
    try {
      const res = await axios.patch(`${API}/travels/trips/${tripId}/budget`, updates);
      setBudget(res.data);
      toast.success('Budget updated');
      setEditMode(false);
    } catch (e) {
      toast.error('Failed to update budget');
    }
  };
  
  const addLineItem = async () => {
    if (!newItem.name) {
      toast.error('Item name is required');
      return;
    }
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/budget/line-items`, newItem);
      setBudget(res.data.budget);
      toast.success('Item added');
      setNewItem({ name: '', category: 'misc_fees', estimated_cost: 0, quantity: 1, notes: '' });
      setAddingItem(false);
    } catch (e) {
      toast.error('Failed to add item');
    }
  };
  
  const deleteLineItem = async (itemId) => {
    if (!confirm('Delete this budget item?')) return;
    try {
      const res = await axios.delete(`${API}/travels/trips/${tripId}/budget/line-items/${itemId}`);
      setBudget(res.data);
      toast.success('Item removed');
    } catch (e) {
      toast.error('Failed to delete item');
    }
  };
  
  const acknowledgeBudgetAlert = async () => {
    await updateBudget({ alert_acknowledged: true });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-[#C8A85A]" />
      </div>
    );
  }
  
  const status = budget?.status || {};
  const totalBudget = budget?.total_budget || 0;
  const totalSpent = status.total_spent || 0;
  const remaining = status.remaining || 0;
  const percentUsed = status.percentage_used || 0;
  const alertThreshold = status.alert_threshold;
  const isOverBudget = remaining < 0;
  
  // Alert styling
  const getAlertStyle = () => {
    if (alertThreshold === 'over_budget') {
      return { bg: 'bg-[#B37E6B]/10', border: 'border-[#B37E6B]/30', text: 'text-[#B37E6B]' };
    }
    if (alertThreshold === 'high_risk') {
      return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' };
    }
    if (alertThreshold === 'early_warning') {
      return { bg: 'bg-[#C8A85A]/10', border: 'border-[#C8A85A]/30', text: 'text-[#C8A85A]' };
    }
    return { bg: 'bg-[#F9F8F4]', border: 'border-[#E6E4DE]', text: 'text-[#595959]' };
  };
  
  const alertStyle = getAlertStyle();
  
  return (
    <div className="space-y-4" data-testid="structured-budget-tracker">
      {/* Budget Alert Banner (if any) */}
      {alertThreshold && !budget?.alert_acknowledged && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border ${alertStyle.bg} ${alertStyle.border}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 ${alertStyle.text} mt-0.5`} />
              <div>
                <p className={`font-medium ${alertStyle.text}`}>
                  {alertThreshold === 'over_budget' && 'Over Budget'}
                  {alertThreshold === 'high_risk' && 'High Risk'}
                  {alertThreshold === 'early_warning' && 'Approaching Budget'}
                </p>
                <p className="text-sm text-[#595959] mt-1">
                  {alertThreshold === 'over_budget' && `Trip is over budget by $${Math.abs(remaining).toLocaleString()}`}
                  {alertThreshold === 'high_risk' && `Trip is at ${percentUsed.toFixed(0)}% of budget. Review remaining plans.`}
                  {alertThreshold === 'early_warning' && `Trip is approaching budget (${percentUsed.toFixed(0)}% used)`}
                </p>
              </div>
            </div>
            <button 
              onClick={acknowledgeBudgetAlert}
              className="text-xs text-[#8C8C8C] hover:text-[#1A1A1A]"
            >
              Acknowledge
            </button>
          </div>
        </motion.div>
      )}
      
      {/* Budget Summary Card */}
      <div className={`p-4 rounded-lg border ${isOverBudget ? 'bg-[#B37E6B]/5 border-[#B37E6B]/20' : 'bg-[#F9F8F4] border-[#E6E4DE]'}`}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-[#8C8C8C]">Total Budget</span>
          {editMode ? (
            <div className="flex items-center gap-2">
              <Select value={budget?.currency || 'USD'} onValueChange={(v) => setBudget({...budget, currency: v})}>
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                value={budget?.total_budget || ''}
                onChange={(e) => setBudget({...budget, total_budget: parseFloat(e.target.value) || 0})}
                className="w-32 h-8"
              />
              <Button size="sm" onClick={() => updateBudget({ total_budget: budget?.total_budget, currency: budget?.currency })}>
                <Save className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xl font-serif">
                {budget?.currency || 'USD'} {totalBudget.toLocaleString()}
              </span>
              <button onClick={() => setEditMode(true)} className="p-1 hover:bg-white rounded">
                <Edit2 className="w-3 h-3 text-[#8C8C8C]" />
              </button>
            </div>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="relative h-3 bg-[#E6E4DE] rounded-full overflow-hidden mb-2">
          {/* 80% marker */}
          <div className="absolute left-[80%] top-0 bottom-0 w-px bg-[#C8A85A]/50 z-10" />
          {/* 90% marker */}
          <div className="absolute left-[90%] top-0 bottom-0 w-px bg-amber-400/50 z-10" />
          {/* Spent bar */}
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(percentUsed, 100)}%` }}
            transition={{ duration: 0.5 }}
            className={`h-full ${
              isOverBudget ? 'bg-[#B37E6B]' : 
              alertThreshold === 'high_risk' ? 'bg-amber-500' :
              alertThreshold === 'early_warning' ? 'bg-[#C8A85A]' :
              'bg-[#8DA399]'
            }`}
          />
        </div>
        
        {/* Stats Row */}
        <div className="flex justify-between text-xs">
          <span className={isOverBudget ? 'text-[#B37E6B] font-medium' : 'text-[#8C8C8C]'}>
            Spent: {budget?.currency || 'USD'} {totalSpent.toLocaleString()} ({percentUsed.toFixed(0)}%)
          </span>
          <span className={isOverBudget ? 'text-[#B37E6B] font-medium' : 'text-[#8DA399]'}>
            {isOverBudget ? 'Over by' : 'Remaining'}: {budget?.currency || 'USD'} {Math.abs(remaining).toLocaleString()}
          </span>
        </div>
      </div>
      
      {/* Category Breakdown Toggle */}
      <button
        onClick={() => setShowLineItems(!showLineItems)}
        className="w-full flex items-center justify-center gap-2 text-sm text-[#8C8C8C] hover:text-[#1A1A1A] py-2"
      >
        {showLineItems ? 'Hide' : 'Show'} Categories & Line Items
        {showLineItems ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {/* Category Breakdown & Line Items */}
      <AnimatePresence>
        {showLineItems && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-4"
          >
            {/* Categories */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {BUDGET_CATEGORIES.map(cat => {
                const catStatus = status.category_status?.[cat.key] || {};
                const catSpent = catStatus.spent || 0;
                const catBudget = catStatus.budget || 0;
                const Icon = cat.icon;
                
                return (
                  <div key={cat.key} className="p-3 bg-white rounded-lg border border-[#E6E4DE]">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-[#8C8C8C]" />
                      <span className="text-xs font-medium text-[#1A1A1A]">{cat.label}</span>
                    </div>
                    <p className="text-sm font-serif text-[#1A1A1A]">
                      ${catSpent.toLocaleString()}
                    </p>
                    {catBudget > 0 && (
                      <div className="mt-1">
                        <div className="h-1 bg-[#E6E4DE] rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              catStatus.alert === 'over_budget' ? 'bg-[#B37E6B]' :
                              catStatus.alert === 'high_risk' ? 'bg-amber-500' :
                              catStatus.alert === 'early_warning' ? 'bg-[#C8A85A]' :
                              'bg-[#8DA399]'
                            }`}
                            style={{ width: `${Math.min(catStatus.percentage_used || 0, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-[#8C8C8C] mt-0.5">
                          of ${catBudget.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Line Items */}
            <div className="border border-[#E6E4DE] rounded-lg overflow-hidden">
              <div className="p-3 bg-[#F9F8F4] border-b border-[#E6E4DE] flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-[#8C8C8C]">
                  Line Items ({budget?.line_items?.length || 0})
                </span>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setAddingItem(true)}
                  className="h-7 text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Item
                </Button>
              </div>
              
              {/* Add Item Form */}
              {addingItem && (
                <div className="p-3 bg-[#C8A85A]/5 border-b border-[#E6E4DE]">
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    <Input
                      placeholder="Item name"
                      value={newItem.name}
                      onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                      className="col-span-2"
                    />
                    <Select value={newItem.category} onValueChange={(v) => setNewItem({...newItem, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_CATEGORIES.map(cat => (
                          <SelectItem key={cat.key} value={cat.key}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Cost"
                      value={newItem.estimated_cost || ''}
                      onChange={(e) => setNewItem({...newItem, estimated_cost: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setAddingItem(false)}>Cancel</Button>
                    <Button size="sm" onClick={addLineItem} className="bg-[#C8A85A] hover:bg-[#B39049]">
                      Add
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Items List */}
              <div className="divide-y divide-[#E6E4DE] max-h-64 overflow-y-auto">
                {budget?.line_items?.length === 0 ? (
                  <div className="p-4 text-center text-sm text-[#8C8C8C]">
                    No line items yet. Add items to track your budget.
                  </div>
                ) : (
                  budget?.line_items?.map(item => {
                    const cat = BUDGET_CATEGORIES.find(c => c.key === item.category);
                    const Icon = cat?.icon || FileText;
                    const cost = (item.actual_cost ?? item.estimated_cost) * (item.quantity || 1);
                    
                    return (
                      <div key={item.id} className="p-3 flex items-center justify-between hover:bg-[#F9F8F4]">
                        <div className="flex items-center gap-3">
                          <Icon className="w-4 h-4 text-[#8C8C8C]" />
                          <div>
                            <p className="text-sm font-medium text-[#1A1A1A]">{item.name}</p>
                            <p className="text-xs text-[#8C8C8C]">
                              {cat?.label || item.category}
                              {item.day_number && ` · Day ${item.day_number}`}
                              {item.linked_itinerary_item_id && (
                                <span className="text-[#C8A85A]"> · Linked</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#1A1A1A]">
                            ${cost.toLocaleString()}
                            {item.quantity > 1 && <span className="text-xs text-[#8C8C8C]"> ×{item.quantity}</span>}
                          </span>
                          <button 
                            onClick={() => deleteLineItem(item.id)}
                            className="p-1 text-[#8C8C8C] hover:text-[#B37E6B]"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* View Full Estimate Link */}
      <button 
        onClick={() => onNavigate?.('prices')}
        className="w-full text-center text-xs text-[#C8A85A] hover:underline flex items-center justify-center gap-1"
      >
        View Full Trip Estimate <ExternalLink className="w-3 h-3" />
      </button>
    </div>
  );
}

// ============================================================
// MAIN UNIFIED TRIP DETAILS COMPONENT
// ============================================================
export function UnifiedTripDetails({ 
  tripId, 
  form, 
  onFormChange, 
  travelers,
  onTravelersChange,
  linkedClient,
  onNavigate,
  clients,
  onClientChange
}) {
  const navigate = useNavigate();
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  const handleChange = (field, value) => {
    onFormChange?.({ ...form, [field]: value });
  };
  
  const toggleTravelStyle = (style) => {
    const current = form.travel_styles || [];
    const updated = current.includes(style)
      ? current.filter(s => s !== style)
      : [...current, style];
    handleChange('travel_styles', updated);
  };
  
  // Calculate if all travelers are booking-ready
  const allBookingReady = travelers?.every(t => t.booking_ready_status?.is_booking_ready) ?? true;
  
  return (
    <div className="space-y-6" data-testid="unified-trip-details">
      {/* All Travelers Booking Ready Alert */}
      {travelers?.length > 0 && !allBookingReady && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-lg border bg-[#B37E6B]/5 border-[#B37E6B]/20"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#B37E6B] mt-0.5" />
            <div>
              <p className="font-medium text-[#B37E6B]">Travelers Not Booking Ready</p>
              <p className="text-sm text-[#595959] mt-1">
                One or more travelers have incomplete profiles required for booking. 
                Complete their profiles below to proceed.
              </p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Section 1: Traveler Profiles + Primary Client Account (MERGED) */}
      <div className="bg-white rounded-xl p-6 border border-[rgba(0,0,0,0.06)]">
        <h2 className="font-serif text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-[#C8A85A]" />
          Traveler Profiles
          {form.is_international && (
            <span className="text-xs px-2 py-0.5 bg-[#C8A85A]/10 text-[#C8A85A] rounded-full">
              <Plane className="w-3 h-3 inline mr-1" />
              International - Passport Required
            </span>
          )}
          {allBookingReady && travelers?.length > 0 && (
            <span className="text-xs px-2 py-0.5 bg-[#8DA399]/10 text-[#8DA399] rounded-full ml-auto">
              <CheckCircle2 className="w-3 h-3 inline mr-1" />
              All Booking Ready
            </span>
          )}
        </h2>

        {/* Primary Client Account — inside Traveler section */}
        <div className="p-3 bg-[#F9F8F4] rounded-lg mb-4" data-testid="trip-client-section">
          <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Primary Client Account</Label>
          {linkedClient ? (
            <div className="flex items-center justify-between mt-1">
              <button
                onClick={() => navigate(`/advisor/manage/clients/${linkedClient.id}`)}
                className="flex items-center gap-2 hover:bg-[#E6E4DE] rounded-lg p-1 -ml-1 transition-colors"
                data-testid="trip-client-link"
              >
                <div className="w-8 h-8 rounded-full bg-[#E6E4DE] flex items-center justify-center">
                  <User className="w-4 h-4 text-[#8C8C8C]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">{linkedClient.full_name}</p>
                  <p className="text-xs text-[#8C8C8C]">{linkedClient.email || 'No email'}</p>
                </div>
              </button>
              <button
                onClick={() => setShowClientPicker(true)}
                className="text-xs text-[#C8A85A] hover:underline"
                data-testid="change-client-btn"
              >
                Change
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowClientPicker(true)}
              className="mt-1 w-full p-2 border border-dashed border-[#C8A85A] rounded-lg text-sm text-[#C8A85A] hover:bg-[#C8A85A]/5 transition-colors"
              data-testid="link-client-btn"
            >
              + Link Client Account
            </button>
          )}
          {/* Client Picker Dropdown */}
          {showClientPicker && (
            <div className="mt-2 bg-white rounded-lg border border-[rgba(0,0,0,0.1)] shadow-md p-3" data-testid="client-picker">
              <Input
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Search clients..."
                className="mb-2 text-sm bg-[#F9F8F4]"
                data-testid="client-picker-search"
                autoFocus
              />
              <div className="max-h-[200px] overflow-y-auto space-y-1">
                {(clients || [])
                  .filter(c => !clientSearch || c.full_name?.toLowerCase().includes(clientSearch.toLowerCase()) || c.email?.toLowerCase().includes(clientSearch.toLowerCase()))
                  .map(c => (
                    <button
                      key={c.id}
                      onClick={() => {
                        handleChange('client_id', c.id);
                        handleChange('client_name', c.full_name);
                        onClientChange?.(c);
                        setShowClientPicker(false);
                        setClientSearch('');
                        toast.success(`Linked to ${c.full_name}`);
                      }}
                      className={`w-full p-2 text-left text-sm rounded hover:bg-[#F5F1EB] flex items-center gap-2 ${
                        c.id === form.client_id ? 'bg-[#F5F1EB]' : ''
                      }`}
                      data-testid={`pick-client-${c.id}`}
                    >
                      <div className="w-6 h-6 rounded-full bg-[#E6E4DE] flex items-center justify-center flex-shrink-0">
                        <User className="w-3 h-3 text-[#8C8C8C]" />
                      </div>
                      <div>
                        <p className="font-medium">{c.full_name}</p>
                        <p className="text-xs text-[#8C8C8C]">{c.email || ''}</p>
                      </div>
                    </button>
                  ))}
                {(clients || []).length === 0 && (
                  <p className="text-sm text-[#8C8C8C] text-center py-2">No clients found</p>
                )}
              </div>
              <button
                onClick={() => { setShowClientPicker(false); setClientSearch(''); }}
                className="text-xs text-[#8C8C8C] mt-2 hover:underline w-full text-center"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <TravelerProfileManager
          tripId={tripId}
          travelers={travelers}
          onTravelersChange={onTravelersChange}
          isInternational={form.is_international}
          travelPartyId={form.travel_party_id}
        />
      </div>
      
      {/* Section 2: Trip Identity + Details (MERGED) */}
      <div className="bg-white rounded-xl p-6 border border-[rgba(0,0,0,0.06)]">
        <h2 className="font-serif text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#C8A85A]" />
          Trip Information
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Core Info */}
          <div className="space-y-4">
            {/* Trip Title/Name - EDITABLE */}
            <div>
              <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Trip Title / Name *</Label>
              <Input 
                value={form.internal_trip_name || ''} 
                onChange={(e) => handleChange('internal_trip_name', e.target.value)}
                placeholder="Jackson's 20th Anniversary Trip"
                className="mt-1 bg-[#EEE9E2]"
                data-testid="trip-title-input"
              />
              <p className="text-xs text-[#8C8C8C] mt-1">
                This is how you'll find this trip in search
              </p>
            </div>
            
            {/* Trip Dates (Home-Based, Date Only) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Depart Home *</Label>
                <Input 
                  type="date" 
                  value={form.trip_start_date || form.start_date || ''} 
                  onChange={(e) => {
                    handleChange('trip_start_date', e.target.value);
                    handleChange('start_date', e.target.value); // Keep legacy field in sync
                  }} 
                  className="mt-1 bg-[#EEE9E2]" 
                  data-testid="trip-start-date"
                />
                <p className="text-[10px] text-[#8C8C8C] mt-1">When client leaves home</p>
              </div>
              <div>
                <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Return Home *</Label>
                <Input 
                  type="date" 
                  value={form.trip_end_date || form.end_date || ''} 
                  onChange={(e) => {
                    handleChange('trip_end_date', e.target.value);
                    handleChange('end_date', e.target.value); // Keep legacy field in sync
                  }} 
                  min={form.trip_start_date || form.start_date || ''}
                  className="mt-1 bg-[#EEE9E2]" 
                  data-testid="trip-end-date"
                />
                <p className="text-[10px] text-[#8C8C8C] mt-1">When client returns home</p>
              </div>
            </div>
            
            {/* Destination Segments (Multi-Destination Support) */}
            <DestinationSegmentsForm
              segments={form.destination_segments || []}
              onChange={(segments) => {
                handleChange('destination_segments', segments);
                // Auto-update legacy destinations field for compatibility
                const destinationNames = segments
                  .map(s => s.destination_name)
                  .filter(Boolean)
                  .join(' → ');
                if (destinationNames) {
                  handleChange('destinations', destinationNames);
                }
              }}
              tripStartDate={form.trip_start_date || form.start_date}
              tripEndDate={form.trip_end_date || form.end_date}
              tripId={form.id}
            />
            
            <div>
              <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Trip Summary / Intent</Label>
              <Textarea 
                value={form.trip_summary || ''} 
                onChange={(e) => handleChange('trip_summary', e.target.value)}
                placeholder="A romantic anniversary getaway exploring the French countryside..."
                className="mt-1 bg-[#EEE9E2] min-h-[80px]"
              />
            </div>
            
            {/* International Flag - FIXED to properly save */}
            <div className="flex items-center justify-between p-3 bg-[#F9F8F4] rounded-lg">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#C8A85A]" />
                <span className="text-sm">International Trip</span>
              </div>
              <Switch 
                checked={Boolean(form.is_international)}
                onCheckedChange={(checked) => {
                  handleChange('is_international', checked);
                }}
                data-testid="international-toggle"
              />
            </div>
            {form.is_international && (
              <p className="text-xs text-[#C8A85A] flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Passport information required for all travelers
              </p>
            )}
          </div>
          
          {/* Right Column - Identity & Style */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Trip Type</Label>
                <Select value={form.trip_type || ''} onValueChange={(v) => handleChange('trip_type', v)}>
                  <SelectTrigger className="mt-1 bg-[#EEE9E2]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIP_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Travelers</Label>
                <Input 
                  type="number" 
                  min="1" 
                  value={form.num_travelers || ''} 
                  onChange={(e) => handleChange('num_travelers', parseInt(e.target.value) || 1)} 
                  className="mt-1 bg-[#EEE9E2]" 
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Travel Style Tags</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TRAVEL_STYLE_OPTIONS.map(style => (
                  <button
                    key={style.value}
                    onClick={() => toggleTravelStyle(style.value)}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                      form.travel_styles?.includes(style.value)
                        ? 'bg-[#C8A85A] text-white'
                        : 'bg-[#F5F5F5] text-[#595959] hover:bg-[#EBEBEB]'
                    }`}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Hero Image for Client Portal */}
            <div className="p-3 bg-[#F9F8F4] rounded-lg" data-testid="trip-hero-image-section">
              <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Member Suite Header Image</Label>
              {form.hero_image_url ? (
                <div className="mt-2 relative group">
                  <img 
                    src={form.hero_image_url} 
                    alt="Member Suite header" 
                    className="w-full h-24 object-cover rounded-lg"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/travels/trips/${tripId}/auto-hero-image`, { method: 'POST' });
                          const data = await res.json();
                          if (data.hero_image_url) {
                            handleChange('hero_image_url', data.hero_image_url);
                            toast.success('New image loaded');
                          }
                        } catch (e) {
                          toast.error('Failed to fetch image');
                        }
                      }}
                      className="text-xs px-2 py-1 bg-white/90 text-[#1A1A1A] rounded hover:bg-white transition-colors"
                      data-testid="regenerate-hero-btn"
                    >
                      Refresh
                    </button>
                    <button
                      onClick={() => handleChange('hero_image_url', '')}
                      className="text-xs px-2 py-1 bg-white/90 text-red-600 rounded hover:bg-white transition-colors"
                      data-testid="remove-hero-btn"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/travels/trips/${tripId}/auto-hero-image`, { method: 'POST' });
                      const data = await res.json();
                      if (data.hero_image_url) {
                        handleChange('hero_image_url', data.hero_image_url);
                        toast.success(`Auto-loaded image for ${data.destination}`);
                      } else {
                        toast.error(data.error || 'No image found');
                      }
                    } catch (e) {
                      toast.error('Failed to fetch image');
                    }
                  }}
                  className="mt-1 w-full p-3 border border-dashed border-[#C8A85A] rounded-lg text-sm text-[#C8A85A] hover:bg-[#C8A85A]/5 transition-colors flex items-center justify-center gap-2"
                  data-testid="auto-hero-btn"
                >
                  <Image className="w-4 h-4" />
                  Auto-Generate Destination Image
                </button>
              )}
              <Input
                value={form.hero_image_url || ''}
                onChange={(e) => handleChange('hero_image_url', e.target.value)}
                placeholder="Or paste a custom image URL..."
                className="mt-2 text-xs bg-[#EEE9E2]"
                data-testid="hero-image-url-input"
              />
            </div>
            
            {/* Internal Trip ID */}
            {form.system_trip_id && (
              <div className="p-3 bg-[#F9F8F4] rounded-lg">
                <Label className="text-xs text-[#8C8C8C] uppercase tracking-wide">Internal Trip ID</Label>
                <p className="text-sm font-mono mt-1">{form.system_trip_id}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Section 3: Structured Budget Tracker */}
      <div className="bg-white rounded-xl p-6 border border-[rgba(0,0,0,0.06)]">
        <h2 className="font-serif text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-[#C8A85A]" />
          Budget Tracker
        </h2>
        <StructuredBudgetTracker tripId={tripId} onNavigate={onNavigate} />
      </div>
      
      {/* Section 4: Consult Notes Reference (Expandable) */}
      {form.consult_notes && (
        <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)]">
          <button 
            onClick={() => onNavigate?.('advisor_notes')}
            className="w-full p-4 flex items-center justify-between hover:bg-[#F9F8F4] transition-colors"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#8C8C8C]" />
              <span className="text-sm text-[#595959]">Consult Notes Available</span>
            </div>
            <ExternalLink className="w-4 h-4 text-[#8C8C8C]" />
          </button>
        </div>
      )}
    </div>
  );
}

export default UnifiedTripDetails;
